import React from "react";
import { Helmet } from "react-helmet";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageTitle } from "@/components/PageTitle";
import { CTASection } from "@/components/CTASection";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { ServiceOverviewSection } from "@/components/services/ServiceOverviewSection";
import { ServiceFeaturesSection } from "@/components/services/ServiceFeaturesSection";
import { WhyChooseUsSection } from "@/components/services/WhyChooseUsSection";
import { ServiceFAQSection } from "@/components/services/ServiceFAQSection";
import { 
  Clock, Zap, Shield, CheckCircle2, Award, Crown, Settings
} from "lucide-react";

const SingleDayRCT: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Single Day RCT in Nagpur | Cost of Single Day RCT in Nagpur</title>
        <meta
          name="description"
          content="Get your root canal treatment completed in just one visit at Asrani Dental Clinic in Nagpur. Efficient, painless single-day RCT with advanced technology. Book your appointment today!"
        />
      </Helmet>

      <Header />

      <main className="pt-20">
        <PageTitle title="Single Day RCT" />

        {/* Service Overview Section */}
        <ServiceOverviewSection
          title="Single Day RCT at Asrani Dental, Sadar"
          description={
            <>
              <p className="mb-4">
                Traditional root canal treatments often require multiple dental visits, but at Asrani Dental Clinic in Nagpur, we offer advanced Single Day Root Canal Treatment (RCT) that can be completed in just one visit. This innovative approach uses state-of-the-art technology and techniques to efficiently save your infected tooth while minimizing your time in the dental chair.
              </p>
              <p className="mb-4">
                Our experienced endodontic specialists use precision instruments and advanced imaging to thoroughly clean, disinfect, and seal your tooth's root canals in a single appointment, providing immediate relief from pain and infection.
              </p>
              <p className="mb-6">
                Single Day RCT at our clinic combines convenience with excellence, ensuring you receive the highest standard of care without disrupting your busy schedule.
              </p>
            </>
          }
          featureIcon={Clock}
          featureTitle="Time-Efficient Treatment"
          featureDescription="Save time with our efficient single-appointment root canal treatment that delivers the same excellent results as traditional multi-visit RCT."
          imageSrc="/lovable-uploads/9a154b62-770d-4d8b-ba3e-69b5329384d0.png"
          imageAlt="Single Day Root Canal Treatment"
        />

        {/* Treatment Features */}
        <ServiceFeaturesSection
          title="Our Single Day RCT Features"
          subtitle="Advanced Endodontic Care in One Convenient Visit"
          features={[
            {
              icon: Settings,
              title: "Advanced Technology",
              description: "We use state-of-the-art rotary instruments and digital imaging to perform more precise and efficient root canal procedures than traditional methods."
            },
            {
              icon: Clock,
              title: "Time-Saving",
              description: "Complete your entire root canal treatment in just one visit, eliminating the need for multiple appointments and saving you valuable time."
            },
            {
              icon: Shield,
              title: "Painless Procedure",
              description: "Our advanced techniques and effective anesthesia ensure your single-day RCT experience is comfortable and virtually pain-free."
            },
            {
              icon: Zap,
              title: "Immediate Relief",
              description: "Get prompt relief from tooth pain and infection in a single visit, rather than waiting between multiple appointments while symptoms persist."
            },
            {
              icon: CheckCircle2,
              title: "Comprehensive Treatment",
              description: "We complete the entire process—diagnosis, cleaning, shaping, disinfection, and sealing of the canals—in one efficient session without compromising quality."
            },
            {
              icon: Crown,
              title: "Same-Day Crown Option",
              description: "For suitable cases, we can provide a permanent crown in the same visit, completing your tooth restoration without the need for a temporary crown."
            }
          ]}
        />

        {/* Why Choose Us */}
        <WhyChooseUsSection
          title="Why Choose Us for Single Day RCT"
          subtitle="Expert Endodontic Care at Asrani Dental, Nagpur"
          reasons={[
            {
              number: 1,
              title: "Specialized Expertise",
              description: "Our dentists have advanced training and extensive experience in endodontics, ensuring your single-day RCT is performed with the highest level of skill and precision."
            },
            {
              number: 2,
              title: "Cutting-Edge Technology",
              description: "We've invested in the latest dental technologies, including advanced rotary instruments, digital imaging, and apex locators, to provide faster, more accurate, and more comfortable root canal treatments."
            },
            {
              number: 3,
              title: "Patient Comfort",
              description: "We prioritize your comfort with effective anesthesia, gentle techniques, and a soothing environment to ensure your single-day RCT experience is as pleasant as possible."
            },
            {
              number: 4,
              title: "High Success Rate",
              description: "Our single-day RCT procedures have an excellent success rate, effectively saving infected teeth and providing long-lasting relief from pain and infection."
            }
          ]}
        />

        {/* Testimonials Section */}
        <ServiceTestimonials
          testimonials={[
            {
              name: "Rahul Mehta",
              comment: "I was dreading a root canal that would take multiple visits, but the single day RCT at Asrani Dental was a game-changer! The procedure was comfortable and I walked out the same day with my tooth completely treated.",
              rating: 5,
              image: "/placeholder.svg"
            },
            {
              name: "Meena Patel",
              comment: "Dr. Asrani completed my root canal in one visit and I was amazed at how painless it was. The technology they use is impressive and I appreciated not having to come back multiple times. Highly recommend!",
              rating: 5,
              image: "/placeholder.svg"
            }
          ]}
          title="What Our RCT Patients Say"
          subtitle="Real Experiences from Patients at Asrani Dental Clinic in Nagpur"
        />

        {/* FAQ Section */}
        <ServiceFAQSection
          title="Frequently Asked Questions"
          subtitle="Common Questions About Single Day RCT in Nagpur"
          faqs={[
            {
              question: "Is single day RCT as effective as traditional root canal treatment?",
              answer: "Yes, single day RCT is just as effective as traditional multi-visit root canal treatment. With advanced technology and techniques, we can achieve the same high-quality results in a single visit that previously required multiple appointments."
            },
            {
              question: "Is single day RCT painful?",
              answer: "No, single day RCT is performed under local anesthesia to ensure your comfort throughout the procedure. Most patients report little to no pain during treatment, and any post-treatment discomfort is typically minimal and can be managed with over-the-counter pain relievers."
            },
            {
              question: "How long does a single day RCT appointment take?",
              answer: "A single day RCT typically takes between 1-2 hours, depending on the complexity of your case and the number of canals in the tooth being treated. This is longer than a single appointment in a traditional multi-visit RCT, but saves you time overall by eliminating additional visits."
            },
            {
              question: "Are all teeth eligible for single day RCT?",
              answer: "Most teeth can be treated with single day RCT, but there are some complex cases that may still require multiple visits. These include teeth with severe infection, unusual anatomy, or certain medical conditions. Our dentist will evaluate your specific situation to determine if single day RCT is appropriate for you."
            },
            {
              question: "How soon can I eat after a single day RCT?",
              answer: "You can eat shortly after the procedure, but it's recommended to wait until the numbness from the anesthesia has worn off to prevent accidentally biting your cheek or tongue. It's best to stick with soft foods initially and avoid chewing on the treated tooth until your permanent restoration (crown) is placed."
            }
          ]}
        />

        <CTASection
          title="Experience Faster, Efficient Root Canal Treatment"
          description="Save time with our advanced single day root canal treatment at Asrani Dental Clinic in Nagpur."
          primaryButtonText="Book Your Appointment"
          primaryButtonLink="/dental-appointment-booking"
        />
      </main>

      <Footer />
    </>
  );
};

export default SingleDayRCT;
